import { Box, Grid, Stack, TextField, Typography } from "@mui/material";
import type {
  NominatedPurchaserDetailsView,
  StorageLocationView,
} from "adl-gen/ferovinum/app/api";
import type { FormikForm } from "components/types/formik-types";
import { CurrencyInput } from "components/widgets/inputs/currency-input/currency-input";
import { ExpandableRadio } from "components/widgets/inputs/expandable-radio/expandable-radio";
import {
  type DeliveryPurchaserRequestFormValues,
  PaymentResponsibilityOption,
} from "../organisation-purchase-request-delivery-options-page-view";

export interface DeliveryFeeCardProps {
  form: FormikForm<DeliveryPurchaserRequestFormValues>;
  purchaser: NominatedPurchaserDetailsView;
  storageLocation: StorageLocationView;
  storageLocationSupportsDeliveryCalculations?: boolean;
}

const DeliveryFeeCard = ({
  form,
  purchaser,
  storageLocation,
  storageLocationSupportsDeliveryCalculations,
}: DeliveryFeeCardProps) => {
  const handleDeliveryFeeChange = (
    deliveryFeePaidBy: PaymentResponsibilityOption,
  ) => {
    form.setFieldValue("customDeliveryCost", undefined);
    if (deliveryFeePaidBy === PaymentResponsibilityOption.Seller) {
      form.setFieldValue("additionalDeliveryCost.amount", undefined);
      form.setFieldValue("additionalDeliveryCost.description", undefined);
    }
    form.setFieldValue("deliveryFeePaidBy", deliveryFeePaidBy);
  };

  const radioOptions = [
    {
      value: PaymentResponsibilityOption.Seller,
      label: "Don't charge purchaser for delivery",
    },
    storageLocationSupportsDeliveryCalculations
      ? {
          value: PaymentResponsibilityOption.Purchaser,
          label: `Charge the standard fee as incurred from ${storageLocation.storageLocationName}`,
        }
      : null,
    {
      value: PaymentResponsibilityOption.PurchaserCustom,
      label: "Charge a custom delivery fee",
      children: (
        <Stack>
          <CurrencyInput
            name="customDeliveryCost"
            label="Custom Fee"
            currency={purchaser.currency}
            value={form.values.customDeliveryCost}
            onChange={(e) =>
              form.setFieldValue("customDeliveryCost", e.target.value)
            }
            onBlur={form.handleBlur}
            error={Boolean(
              form.touched.customDeliveryCost && form.errors.customDeliveryCost,
            )}
            helperText={
              form.touched.customDeliveryCost && form.errors.customDeliveryCost
            }
          />
        </Stack>
      ),
    },
  ].filter(Boolean);

  const showAdditionalCosts =
    form.values.deliveryFeePaidBy &&
    form.values.deliveryFeePaidBy !== PaymentResponsibilityOption.Seller;
  return (
    <Stack direction="column" spacing={4}>
      <Box sx={{ pl: 2, mt: 1 }}>
        <ExpandableRadio
          onChange={handleDeliveryFeeChange}
          value={form.values.deliveryFeePaidBy}
          options={radioOptions}
        />
      </Box>

      {showAdditionalCosts && (
        <>
          <Typography variant="body1">
            If applicable, please indicate any additional packaging or shipping
            costs you would like to charge to your purchaser.
          </Typography>

          <Grid sx={{ pl: 4 }} container spacing={2}>
            <Grid item xs={2}>
              <TextField
                name="additionalDeliveryCostDescription"
                label="Cost Description"
                value={form.values.additionalDeliveryCost?.description}
                onChange={(e) =>
                  form.setFieldValue(
                    "additionalDeliveryCost.description",
                    e.target.value,
                  )
                }
                onBlur={form.handleBlur}
                fullWidth
                placeholder="e.g. Additional Packaging Fees"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={1}>
              <CurrencyInput
                name="additionalDeliveryCostPrice"
                label="Price"
                value={form.values.additionalDeliveryCost?.amount}
                currency={purchaser.currency}
                onChange={(e) =>
                  form.setFieldValue(
                    "additionalDeliveryCost.amount",
                    e.target.value,
                  )
                }
                onBlur={form.handleBlur}
                fullWidth
              />
            </Grid>
          </Grid>
        </>
      )}
    </Stack>
  );
};

export default DeliveryFeeCard;
